import styled, { css } from 'styled-components'
import { FlexBox, MEDIA } from '@brg/ui'

const scrollableControlThumb = css`
  scrollbar-width: thin;
  scrollbar-color: #909090 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: #909090;
    border: 1.55px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
  }
`

export const QuestionsSectionTitle = styled.h2`
  margin: 0 0 24px;
  font-family: Inter, sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  color: #151514;

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    line-height: 25px;
  }
`

export const QuestionsDroppableList = styled.div`
  display: flex;
  flex-direction: column;
`

export const QuestionFormCard = styled.div<{ $saved?: boolean }>`
  background: #f2f2f2;
  border-radius: 3px;
  padding: ${(p) => (p.$saved ? '16px' : '24px')};
  margin-bottom: 24px;
  box-sizing: border-box;

  @media ${MEDIA.MOBILE} {
    padding: ${(p) => (p.$saved ? '12px' : '16px')};
  }
`

export const QuestionCardDragRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`

export const SavedQuestionRoot = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas: 'handle body actions';
  align-items: start;
  column-gap: 16px;
  row-gap: 16px;
  width: 100%;

  @media ${MEDIA.MOBILE} {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'handle kind actions'
      'body body body';
    column-gap: 8px;
    row-gap: 8px;
  }
`

export const SavedDesktopHandle = styled.div`
  grid-area: handle;
  flex-shrink: 0;

  @media ${MEDIA.MOBILE} {
    align-self: center;
  }
`

export const SavedBody = styled.div`
  grid-area: body;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const SavedKindLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.LAPTOP} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`

export const SavedDesktopKindLabel = styled(SavedKindLabel)`
  @media ${MEDIA.MOBILE} {
    display: none;
  }
`

export const SavedMobileKindLabel = styled(SavedKindLabel)`
  display: none;
  grid-area: kind;
  align-self: center;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    display: block;
  }
`

export const SavedActions = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media ${MEDIA.MOBILE} {
    align-self: center;
  }
`

export const SavedActionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  color: #909090;

  &:hover {
    color: #151514;
    background: rgba(0, 0, 0, 0.04);
  }

  svg {
    display: block;
  }
`

export const SavedQuestionTitle = styled.div<{ $collapsed?: boolean }>`
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
  color: #151514;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  ${(p) =>
    p.$collapsed
      ? css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 4;
          overflow: hidden;
        `
      : ''}
`

export const ShowFullToggle = styled.button<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #909090;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;

  svg {
    display: block;
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  }

  path {
    fill: currentColor;
  }

  &:hover {
    color: #545453;
  }
`

export const SavedQuestionImageCenter = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

export const SavedQuestionImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  background: #e7e7e7;
`

export const SavedOptionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const SavedOptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

export const SavedOptionBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const SavedOptionText = styled.div`
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #151514;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.LAPTOP} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`

export const SavedOptionMark = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: default;
  pointer-events: none;

  &[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #838382;
    border-radius: 50%;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &[type='radio']:checked::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #151514;
  }
`

export const SavedExplanationSection = styled.div`
  width: 100%;
`

export const SavedExplanationLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 16px;
  color: #909090;
  margin-bottom: 6px;
`

export const SavedExplanationText = styled.div`
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #151514;
  white-space: pre-wrap;

  @media ${MEDIA.LAPTOP} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`

export const FormBlock = styled.div`
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const inputHeightMedia = css`
  height: 48px;
  min-height: 48px;
  max-height: 48px;
  box-sizing: border-box;

  @media (max-width: 1440px), (max-height: 920px) {
    height: 40px;
    min-height: 40px;
    max-height: 40px;
  }
`

export const SurveyTypeDropdownWrap = styled.div`
  width: 100%;
  position: relative;

  .ui-input_container {
    background-color: #ffffff !important;
    ${inputHeightMedia}
  }

  .dropdown-element-popup,
  .dropdown-element-content {
    background: #ffffff !important;
  }

  .dropdown-list-item {
    background: #ffffff !important;

    &:hover {
      background: ${({ theme }) =>
        theme.navigationBar?.login?.dropDownHoverBackground ??
        '#E7E7E7'} !important;
    }
  }
`

const starAfter = css`
  &::after {
    position: absolute;
    top: 0;
    right: 0;
    content: '*';
    color: #c64839;
    font-weight: 600;
  }
`

export const FieldLabel = styled.label<{ $required?: boolean }>`
  display: block;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 8px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: #909090;

  ${(p) =>
    p.$required &&
    css`
      position: relative;
      padding-right: 10px;
      ${starAfter}
    `}
`

export const InlineFieldLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: #909090;
`

export const LabelRow = styled(FlexBox)`
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

export const QuestionTextRow = styled(FlexBox)`
  align-items: flex-end;
  gap: 12px;
  width: 100%;
`

export const QuestionInputColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const QuestionTextareaWrap = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  width: 100%;
  height: 173px;
  min-height: 173px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 3px;
  resize: vertical;
  overflow: hidden;

  &:focus-within {
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const QuestionTextarea = styled.textarea`
  display: block;
  width: auto;
  height: auto;
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  justify-self: stretch;
  box-sizing: border-box;
  /* Chrome drops textarea padding once the field scrolls, so the inset lives on the margin. */
  margin: 14px 16px 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;
  resize: none;
  overflow: auto;

  ${scrollableControlThumb}

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
  }
`

export const CharCounter = styled.span<{ $footer?: boolean }>`
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: #909090;
  pointer-events: none;

  ${(p) =>
    p.$footer &&
    css`
      position: static;
      display: block;
      width: 100%;
      box-sizing: border-box;
      text-align: right;
      padding: 12px 20px 11px 16px;
    `}
`

export const ImagePreviewBox = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`

export const AttachedImageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

export const ImagePreviewImg = styled.img`
  display: block;
  max-width: min(100%, 280px);
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  background: #e7e7e7;
`

export const SubTitle = styled.h3<{ $required?: boolean }>`
  margin: 0 0 12px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  color: #151514;

  ${(p) =>
    p.$required &&
    css`
      position: relative;
      display: inline-block;
      padding-right: 10px;
      ${starAfter}
    `}
`

export const HintText = styled.p`
  margin: 0 0 12px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #909090;
`

export const MarkCorrectHintRow = styled(FlexBox)`
  margin: 0 0 12px;
  align-items: center;
  gap: 10px;
  margin-left: 46px;
`

export const MarkCorrectHintDummy = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin: 0;
  cursor: default;
  pointer-events: none;

  &[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #838382;
    border-radius: 50%;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &[type='radio']:checked::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #151514;
  }
`

export const MarkCorrectHintLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #909090;
`

export const RadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const AddTagButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid #909090;
  border-radius: 5px;
  background: transparent;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
  text-transform: lowercase;
  align-self: flex-start;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`

export const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  background: transparent;
  padding: 0;
`

export const OptionFieldColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const DragHandle = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: grab;
  flex-shrink: 0;
  color: #909090;

  &:active {
    cursor: grabbing;
  }
`

export const OptionInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
  border: none;
  border-radius: 3px;
  background: #fff;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;
  ${inputHeightMedia}

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const OptionImagePreviewImg = styled.img`
  display: block;
  max-width: min(100%, 280px);
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  background: #e7e7e7;
`

export const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  color: #909090;
  margin-top: 2px;

  &:hover {
    color: #151514;
    background: rgba(0, 0, 0, 0.04);
  }
`

export const SavedHeaderIconBtn = styled(IconBtn)`
  margin-top: 0;
  align-self: flex-start;
  position: relative;
  z-index: 2;
`

export const OptionActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 2px;
  flex-shrink: 0;
  padding-top: 2px;
`

export const ExplanationWrap = styled.div`
  position: relative;
  width: 100%;
`

export const ExplanationTextarea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  padding: 12px 16px 28px;
  border: none;
  border-radius: 3px;
  background: #fff;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;
  resize: vertical;

  ${scrollableControlThumb}

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const FormActions = styled(FlexBox)`
  margin-top: 24px;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

export const HiddenFileInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`

export const OptionMark = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;

  &[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #838382;
    border-radius: 50%;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &[type='radio']:checked::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #151514;
  }
`

export const UiCheckboxWrap = styled.div`
  width: 24px;
  min-width: 24px;
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  .common-checkbox {
    width: 24px !important;
    min-width: 24px !important;
    padding: 0 !important;
    gap: 0 !important;
  }
`
import styled, { css } from 'styled-components'
import { FlexBox, MEDIA } from '@brg/ui'

const scrollableControlThumb = css`
  scrollbar-width: thin;
  scrollbar-color: #909090 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: #909090;
    border: 1.55px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
  }
`

export const QuestionsSectionTitle = styled.h2`
  margin: 0 0 24px;
  font-family: Inter, sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  color: #151514;

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    line-height: 25px;
  }
`

export const QuestionsDroppableList = styled.div`
  display: flex;
  flex-direction: column;
`

export const QuestionFormCard = styled.div<{ $saved?: boolean }>`
  background: #f2f2f2;
  border-radius: 3px;
  padding: ${(p) => (p.$saved ? '16px' : '24px')};
  margin-bottom: 24px;
  box-sizing: border-box;

  @media ${MEDIA.MOBILE} {
    padding: ${(p) => (p.$saved ? '12px' : '16px')};
  }
`

export const QuestionCardDragRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`

export const SavedQuestionRoot = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas: 'handle body actions';
  align-items: start;
  column-gap: 16px;
  row-gap: 16px;
  width: 100%;

  @media ${MEDIA.MOBILE} {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'handle kind actions'
      'body body body';
    column-gap: 8px;
    row-gap: 8px;
  }
`

export const SavedDesktopHandle = styled.div`
  grid-area: handle;
  flex-shrink: 0;

  @media ${MEDIA.MOBILE} {
    align-self: center;
  }
`

export const SavedBody = styled.div`
  grid-area: body;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const SavedKindLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.LAPTOP} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`

export const SavedDesktopKindLabel = styled(SavedKindLabel)`
  @media ${MEDIA.MOBILE} {
    display: none;
  }
`

export const SavedMobileKindLabel = styled(SavedKindLabel)`
  display: none;
  grid-area: kind;
  align-self: center;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    display: block;
  }
`

export const SavedActions = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media ${MEDIA.MOBILE} {
    align-self: center;
  }
`

export const SavedActionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  color: #909090;

  &:hover {
    color: #151514;
    background: rgba(0, 0, 0, 0.04);
  }

  svg {
    display: block;
  }
`

export const SavedQuestionTitle = styled.div<{ $collapsed?: boolean }>`
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
  color: #151514;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  ${(p) =>
    p.$collapsed
      ? css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 4;
          overflow: hidden;
        `
      : ''}
`

export const ShowFullToggle = styled.button<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #909090;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;

  svg {
    display: block;
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  }

  path {
    fill: currentColor;
  }

  &:hover {
    color: #545453;
  }
`

export const SavedQuestionImageCenter = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

export const SavedQuestionImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  background: #e7e7e7;
`

export const SavedOptionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const SavedOptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

export const SavedOptionBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const SavedOptionText = styled.div`
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #151514;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.LAPTOP} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`

export const SavedOptionMark = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: default;
  pointer-events: none;

  &[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #838382;
    border-radius: 50%;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &[type='radio']:checked::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #151514;
  }
`

export const SavedExplanationSection = styled.div`
  width: 100%;
`

export const SavedExplanationLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 16px;
  color: #909090;
  margin-bottom: 6px;
`

export const SavedExplanationText = styled.div`
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #151514;
  white-space: pre-wrap;

  @media ${MEDIA.LAPTOP} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }
`

export const FormBlock = styled.div`
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const inputHeightMedia = css`
  height: 48px;
  min-height: 48px;
  max-height: 48px;
  box-sizing: border-box;

  @media (max-width: 1440px), (max-height: 920px) {
    height: 40px;
    min-height: 40px;
    max-height: 40px;
  }
`

export const SurveyTypeDropdownWrap = styled.div`
  width: 100%;
  position: relative;

  .ui-input_container {
    background-color: #ffffff !important;
    ${inputHeightMedia}
  }

  .dropdown-element-popup,
  .dropdown-element-content {
    background: #ffffff !important;
  }

  .dropdown-list-item {
    background: #ffffff !important;

    &:hover {
      background: ${({ theme }) =>
        theme.navigationBar?.login?.dropDownHoverBackground ??
        '#E7E7E7'} !important;
    }
  }
`

const starAfter = css`
  &::after {
    position: absolute;
    top: 0;
    right: 0;
    content: '*';
    color: #c64839;
    font-weight: 600;
  }
`

export const FieldLabel = styled.label<{ $required?: boolean }>`
  display: block;
  width: fit-content;
  max-width: 100%;
  margin-bottom: 8px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: #909090;

  ${(p) =>
    p.$required &&
    css`
      position: relative;
      padding-right: 10px;
      ${starAfter}
    `}
`

export const InlineFieldLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: #909090;
`

export const LabelRow = styled(FlexBox)`
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

export const QuestionTextRow = styled(FlexBox)`
  align-items: flex-end;
  gap: 12px;
  width: 100%;
`

export const QuestionInputColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const QuestionTextareaWrap = styled.div`
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  width: 100%;
  height: 173px;
  min-height: 173px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 3px;
  resize: vertical;
  overflow: hidden;

  &:focus-within {
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const QuestionTextarea = styled.textarea`
  display: block;
  width: auto;
  height: auto;
  min-width: 0;
  min-height: 0;
  align-self: stretch;
  justify-self: stretch;
  box-sizing: border-box;
  /* Chrome drops textarea padding once the field scrolls, so the inset lives on the margin. */
  margin: 14px 16px 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;
  resize: none;
  overflow: auto;

  ${scrollableControlThumb}

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
  }
`

export const CharCounter = styled.span<{ $footer?: boolean }>`
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: #909090;
  pointer-events: none;

  ${(p) =>
    p.$footer &&
    css`
      position: static;
      display: block;
      width: 100%;
      box-sizing: border-box;
      text-align: right;
      padding: 12px 20px 11px 16px;
    `}
`

export const ImagePreviewBox = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`

export const AttachedImageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

export const ImagePreviewImg = styled.img`
  display: block;
  max-width: min(100%, 280px);
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  background: #e7e7e7;
`

export const SubTitle = styled.h3<{ $required?: boolean }>`
  margin: 0 0 12px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  color: #151514;

  ${(p) =>
    p.$required &&
    css`
      position: relative;
      display: inline-block;
      padding-right: 10px;
      ${starAfter}
    `}
`

export const HintText = styled.p`
  margin: 0 0 12px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #909090;
`

export const MarkCorrectHintRow = styled(FlexBox)`
  margin: 0 0 12px;
  align-items: center;
  gap: 10px;
  margin-left: 46px;
`

export const MarkCorrectHintDummy = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin: 0;
  cursor: default;
  pointer-events: none;

  &[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #838382;
    border-radius: 50%;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &[type='radio']:checked::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #151514;
  }
`

export const MarkCorrectHintLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #909090;
`

export const RadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const AddTagButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid #909090;
  border-radius: 5px;
  background: transparent;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
  text-transform: lowercase;
  align-self: flex-start;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`

export const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  background: transparent;
  padding: 0;
`

export const OptionFieldColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const DragHandle = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: grab;
  flex-shrink: 0;
  color: #909090;

  &:active {
    cursor: grabbing;
  }
`

export const OptionInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
  border: none;
  border-radius: 3px;
  background: #fff;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;
  ${inputHeightMedia}

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const OptionImagePreviewImg = styled.img`
  display: block;
  max-width: min(100%, 280px);
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
  background: #e7e7e7;
`

export const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  color: #909090;
  margin-top: 2px;

  &:hover {
    color: #151514;
    background: rgba(0, 0, 0, 0.04);
  }
`

export const SavedHeaderIconBtn = styled(IconBtn)`
  margin-top: 0;
  align-self: flex-start;
  position: relative;
  z-index: 2;
`

export const OptionActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 2px;
  flex-shrink: 0;
  padding-top: 2px;
`

export const ExplanationWrap = styled.div`
  position: relative;
  width: 100%;
`

export const ExplanationTextarea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  padding: 12px 16px 28px;
  border: none;
  border-radius: 3px;
  background: #fff;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  color: #151514;
  resize: vertical;

  ${scrollableControlThumb}

  &::placeholder {
    color: #909090;
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 1px #909090;
  }
`

export const FormActions = styled(FlexBox)`
  margin-top: 24px;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

export const HiddenFileInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`

export const OptionMark = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;

  &[type='radio'] {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid #838382;
    border-radius: 50%;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &[type='radio']:checked::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #151514;
  }
`

export const UiCheckboxWrap = styled.div`
  width: 24px;
  min-width: 24px;
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  .common-checkbox {
    width: 24px !important;
    min-width: 24px !important;
    padding: 0 !important;
    gap: 0 !important;
  }
`
